import React from 'react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const C = { void:'#030A3C', void2:'#060D4A', cyan:'#00FFFF', white:'#F8F9FA', muted:'rgba(248,249,250,0.72)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)' };

const Section = ({ title, children }) => (
  <div style={s.section}>
    <h2 style={s.h2}>{title}</h2>
    {children}
  </div>
);

const Table = ({ rows }) => (
  <table style={s.table}>
    <thead>
      <tr>
        {rows[0].map((h, i) => <th key={i} style={s.th}>{h}</th>)}
      </tr>
    </thead>
    <tbody>
      {rows.slice(1).map((row, i) => (
        <tr key={i}>
          {row.map((cell, j) => <td key={j} style={s.td}>{cell}</td>)}
        </tr>
      ))}
    </tbody>
  </table>
);

export default function Privacy() {
  return (
    <div style={s.root}>
      <MarketingNav />

      <div style={s.hero}>
        <div style={s.heroBg} />
        <div style={s.eyebrow}>Legal</div>
        <h1 style={s.h1}>Privacy Policy</h1>
        <p style={s.heroSub}>Last updated: 20 March 2026</p>
      </div>

      <div style={s.body}>
        <div style={s.inner}>

          <Section title="1. Who We Are">
            <p style={s.p}>SenseCritiq is operated by Salvis Are, Grünauer Str. 68, 12524 Berlin, Germany ("we", "us", "our"). For questions about this policy, contact <a href="mailto:privacy@sensecritiq.com" style={s.link}>privacy@sensecritiq.com</a>.</p>
          </Section>

          <Section title="2. What Data We Collect">
            <p style={s.p}><strong style={s.strong}>Account data.</strong> When you register, we collect your email address and name via Clerk (our authentication provider). This is necessary to provide the service.</p>
            <p style={s.p}><strong style={s.strong}>Research content.</strong> Files you upload (audio, video, transcripts) and the synthesised output (themes, findings, quotes) are stored and processed on your behalf. This content is yours. We process it solely to deliver the service.</p>
            <p style={s.p}><strong style={s.strong}>Usage data.</strong> We log API calls, session counts, and processing activity to power your usage dashboard and enforce quota limits.</p>
            <p style={s.p}><strong style={s.strong}>Payment data.</strong> Billing is handled by Stripe. We do not store card details. Stripe processes your payment information under their own privacy policy.</p>
            <p style={s.p}><strong style={s.strong}>Analytics data.</strong> We use Google Analytics and Microsoft Clarity to understand how users interact with sensecritiq.com. These tools collect anonymised usage data including page views, session duration, clicks, and heatmaps. Analytics cookies are only set with your explicit consent.</p>
          </Section>

          <Section title="3. Legal Basis for Processing (GDPR Art. 6)">
            <Table rows={[
              ['Purpose', 'Legal basis'],
              ['Providing the service', 'Contract (Art. 6(1)(b))'],
              ['Billing and invoicing', 'Legal obligation (Art. 6(1)(c))'],
              ['Analytics and improvement', 'Consent (Art. 6(1)(a))'],
              ['Security and fraud prevention', 'Legitimate interest (Art. 6(1)(f))'],
            ]} />
          </Section>

          <Section title="4. Sub-processors">
            <p style={s.p}>We share data with the following third-party processors to deliver the service. All US-based processors operate under Standard Contractual Clauses (SCCs) or the EU–US Data Privacy Framework.</p>
            <Table rows={[
              ['Processor', 'Purpose', 'Location'],
              ['Clerk', 'Authentication', 'USA'],
              ['Stripe', 'Payment processing', 'USA'],
              ['Railway', 'Backend hosting', 'USA'],
              ['Vercel', 'Frontend hosting', 'USA'],
              ['Cloudflare R2', 'File storage', 'USA / EU'],
              ['AssemblyAI', 'Audio transcription', 'USA'],
              ['Anthropic', 'AI synthesis (Claude)', 'USA'],
              ['OpenAI', 'Embeddings', 'USA'],
              ['Google Analytics', 'Usage analytics', 'USA'],
              ['Microsoft Clarity', 'Session analytics', 'USA'],
            ]} />
          </Section>

          <Section title="5. Research File Confidentiality">
            <p style={s.p}>Your research data is treated as confidential. We do not use your uploaded files, transcripts, or synthesised content to train any AI model. Files are encrypted at rest and in transit. Access is restricted to your account.</p>
          </Section>

          <Section title="6. Data Retention">
            <ul style={s.ul}>
              <li style={s.li}><strong style={s.strong}>Account data:</strong> Retained for the duration of your account plus 30 days after deletion</li>
              <li style={s.li}><strong style={s.strong}>Research content:</strong> Retained until you delete it or close your account</li>
              <li style={s.li}><strong style={s.strong}>Payment records:</strong> Retained for 10 years as required by German tax law (§ 147 AO)</li>
              <li style={s.li}><strong style={s.strong}>Analytics data:</strong> Retained per Google and Microsoft's standard retention periods (up to 14 months for Google Analytics)</li>
            </ul>
          </Section>

          <Section title="7. Your Rights (GDPR)">
            <p style={s.p}>You have the right to access, correct, delete, restrict, or port your personal data. You may also object to processing based on legitimate interest, and withdraw consent for analytics at any time.</p>
            <p style={s.p}>To exercise any right, email <a href="mailto:privacy@sensecritiq.com" style={s.link}>privacy@sensecritiq.com</a>. We will respond within 30 days.</p>
            <p style={s.p}>You have the right to lodge a complaint with the Berlin data protection authority:<br />
              <strong style={s.strong}>Berliner Beauftragte für Datenschutz und Informationsfreiheit</strong><br />
              Friedrichstr. 219, 10969 Berlin — <a href="mailto:mailbox@datenschutz-berlin.de" style={s.link}>mailbox@datenschutz-berlin.de</a>
            </p>
          </Section>

          <Section title="8. Cookies">
            <p style={s.p}>We use strictly necessary cookies for authentication (set by Clerk) and, with your consent, analytics cookies from Google Analytics and Microsoft Clarity. See our <a href="/cookies" style={s.link}>Cookie Policy</a> for full details. You can manage your preferences at any time via the cookie settings link in the footer.</p>
          </Section>

          <Section title="9. Security">
            <p style={s.p}>We use TLS for all data in transit, encryption at rest, and hashed API keys. No method of transmission is 100% secure — we cannot guarantee absolute security.</p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p style={s.p}>We may update this policy from time to time. We will notify you by email at least 14 days before material changes take effect. Continued use after the effective date constitutes acceptance.</p>
          </Section>

          <Section title="Contact">
            <p style={s.p}>Questions about this policy: <a href="mailto:privacy@sensecritiq.com" style={s.link}>privacy@sensecritiq.com</a></p>
          </Section>

        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}

const s = {
  root: { background:C.void, minHeight:'100vh', fontFamily:"'Inter',sans-serif", color:C.white },
  hero: { textAlign:'center', padding:'80px 24px 60px', position:'relative', overflow:'hidden' },
  heroBg: { position:'absolute', inset:0, background:'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,255,255,0.07) 0%, transparent 65%)', pointerEvents:'none' },
  eyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, position:'relative' },
  h1: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(28px,4vw,48px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:14, position:'relative' },
  heroSub: { fontSize:14, color:C.muted2, position:'relative' },
  body: { padding:'0 24px 80px' },
  inner: { maxWidth:1100, margin:'0 auto' },
  section: { borderTop:`1px solid ${C.border}`, paddingTop:36, marginTop:36 },
  h2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:20, fontWeight:700, letterSpacing:'-0.02em', marginBottom:16, color:C.white },
  p: { fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:14 },
  ul: { paddingLeft:20, margin:'0 0 14px' },
  li: { fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:8 },
  strong: { color:C.white, fontWeight:600 },
  link: { color:C.cyan, textDecoration:'none' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:16, fontSize:14 },
  th: { textAlign:'left', padding:'8px 12px', borderBottom:`1px solid ${C.border2}`, color:C.white, fontWeight:600 },
  td: { padding:'8px 12px', borderBottom:`1px solid ${C.border}`, color:C.muted, verticalAlign:'top' },
};
